import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Loader2, User, Calendar, Clock } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { detectUserContext, UserContext } from '../../utils/location'
import { auth } from '@/firebaseConfig'
import { getSecureHeaders } from '@/services'
import { LocationInput } from './LocationInput'
import { BirthTimezoneField, type BirthTimezoneState } from './BirthTimezoneField'
import { isRealCalendarDate, toIsoBirthDate } from '@/utils/birthDate'
import { useTranslation } from 'react-i18next'
import HoroscopeProgressBar from './HoroscopeProgressBar'

interface BirthChartFormProps {
  onChartGenerated: (chartData: unknown) => void
}

const INITIAL_BIRTH_CHART_FORM_DATA = {
  name: '',
  gender: 'male',
  birthDate: '',
  birthTime: '',
  latitude: '',
  longitude: '',
  // No default. A seeded offset is a silent wrong answer for every birth
  // outside India: it looks filled-in, so nothing prompts the user to correct it.
  // Resolved from the birthplace and birth instant by BirthTimezoneField.
  timezoneOffset: '',
  timezoneName: '',
  timezoneSource: '' as '' | 'auto' | 'manual',
  timezoneFold: null as number | null,
  timezoneResolverVersion: null as number | null,
  locationName: '',
  country: '',
  countryName: ''
}

const BirthChartForm = ({ onChartGenerated }: BirthChartFormProps) => {
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [formData, setFormData] = useState(() => ({ ...INITIAL_BIRTH_CHART_FORM_DATA }))
  const [birthTimeDisplay, setBirthTimeDisplay] = useState('')
  const [birthTimeInput, setBirthTimeInput] = useState('')
  const [birthDateInput, setBirthDateInput] = useState('')
  const [birthEra, setBirthEra] = useState<'CE' | 'BCE'>('CE')
  const [birthDateError, setBirthDateError] = useState<string>('')
  const [chartFor, setChartFor] = useState<'self' | 'other' | ''>('')
  const [subjectRelation, setSubjectRelation] = useState('friend')
  const [validationBanner, setValidationBanner] = useState<string>('')
  const [chartForError, setChartForError] = useState<string>('')
  const [realtimeEnabled, setRealtimeEnabled] = useState(false)
  const [realtimeContext, setRealtimeContext] = useState<UserContext>(null)
  const [realtimeDetecting, setRealtimeDetecting] = useState(false)
  const [tzState, setTzState] = useState<BirthTimezoneState>({
    offsetHours: null, timezoneName: null, status: 'idle', usable: false,
    source: null, fold: null, resolverVersion: null,
  })
  const chartForRef = useRef<HTMLDivElement | null>(null)
  const { toast } = useToast()
  const { t } = useTranslation('chat')
  const resetFormTooltip = t('chat.astro_form.reset_form_tooltip', {
    defaultValue: 'Clear all fields and start over',
  })
  const progressStages = [
    {
      from: 0,
      label: t('chat.astro_form.progress.calculating_positions', {
        defaultValue: 'Calculating planetary positions using NASA data',
      }),
    },
    {
      from: 0.18,
      label: t('chat.astro_form.progress.casting_charts', {
        defaultValue: 'Casting your rasi and navamsa charts',
      }),
    },
    {
      from: 0.4,
      label: t('chat.astro_form.progress.mapping_nakshatras', {
        defaultValue: 'Mapping nakshatras and dashas',
      }),
    },
    {
      from: 0.65,
      label: t('chat.astro_form.progress.reading_yogas', {
        defaultValue: 'Reading yogas and drishti aspects',
      }),
    },
    {
      from: 0.88,
      label: t('chat.astro_form.progress.finalising', {
        defaultValue: 'Finalising your horoscope',
      }),
    },
  ]

  const hasResettableFormData = loaded && (
    (Object.keys(INITIAL_BIRTH_CHART_FORM_DATA) as Array<keyof typeof INITIAL_BIRTH_CHART_FORM_DATA>).some((key) => (
      String(formData[key] ?? '').trim() !== String(INITIAL_BIRTH_CHART_FORM_DATA[key] ?? '').trim()
    )) ||
    birthTimeInput.trim() !== '' ||
    birthTimeDisplay.trim() !== '' ||
    birthDateInput.trim() !== '' ||
    birthEra !== 'CE' ||
    chartFor !== '' ||
    subjectRelation !== 'friend' ||
    realtimeEnabled ||
    Boolean(realtimeContext)
  )

  const handleResetForm = () => {
    if (!window.confirm(t('chat.astro_form.clear_details_confirm'))) return
    setFormData({ ...INITIAL_BIRTH_CHART_FORM_DATA })
    setBirthTimeInput('')
    setBirthDateInput('')
    setBirthTimeDisplay('')
    setBirthEra('CE')
    setBirthDateError('')
    setChartFor('')
    setSubjectRelation('friend')
    setChartForError('')
    setValidationBanner('')
    setRealtimeEnabled(false)
    setRealtimeContext(null)
    setRealtimeDetecting(false)
    setTzState({
      offsetHours: null, timezoneName: null, status: 'idle', usable: false,
      source: null, fold: null, resolverVersion: null,
    })
    localStorage.removeItem('birthChartData')
  }

  // Persistence
  useEffect(() => {
    const saved = localStorage.getItem('birthChartData')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Forms saved before the timezone fix hold a seeded `5.5` offset and no
        // IANA zone. Restoring those would re-display a wrong offset as though the
        // user had chosen it, so the timezone fields are dropped and re-resolved.
        const savedForm = { ...(parsed.formData || {}) }
        if (parsed.schemaVersion !== 3 || savedForm.timezoneSource !== 'manual') {
          delete savedForm.timezoneOffset
          delete savedForm.timezoneSource
          delete savedForm.timezoneFold
          delete savedForm.timezoneResolverVersion
        }
        setFormData((prev) => ({ ...prev, ...savedForm }))
        setBirthTimeInput(parsed.timeInput || '')
        setBirthDateInput(parsed.dateInput || '')
        setBirthTimeDisplay(parsed.timeDisplay || '')
        setBirthEra(parsed.era || 'CE')
        setChartFor(parsed.chartFor || '')
        setSubjectRelation(parsed.subjectRelation || 'friend')
      } catch {
        void 0
      }
    }
    setLoaded(true)
  }, [])

  useEffect(() => {
    if (!loaded) return
    const timeoutId = window.setTimeout(() => {
      try {
        localStorage.setItem('birthChartData', JSON.stringify({
          schemaVersion: 3,
          formData,
          timeInput: birthTimeInput,
          dateInput: birthDateInput,
          timeDisplay: birthTimeDisplay,
          era: birthEra,
          chartFor,
          subjectRelation,
        }))
      } catch {
        void 0
      }
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [formData, birthTimeInput, birthDateInput, birthTimeDisplay, birthEra, chartFor, subjectRelation, loaded])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const formatDMY = (raw: string) => {
    const digits = (raw || '').replace(/[^0-9]/g, '')
    const dd = digits.slice(0, 2)
    const mm = digits.slice(2, 4)
    const y = digits.slice(4, 9)
    let out = ''
    if (dd) out += dd
    if (mm) out += `-${mm}`
    if (y) out += `-${y}`
    return { dd, mm, y, display: out }
  }

  const handleBirthDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const eraHint = /--/.test(raw) ? 'BCE' : 'CE'
    const { dd, mm, y, display } = formatDMY(raw)
    const displayWithEra = eraHint === 'BCE' && y ? `${dd}${mm ? `-${mm}` : ''}--${y}` : display
    setBirthEra(eraHint as 'CE' | 'BCE')
    setBirthDateInput(displayWithEra)
    const sign = (eraHint === 'BCE') ? '-' : ''
    const stored = y ? `${dd}-${mm}-${sign}${y}` : displayWithEra
    setFormData(prev => ({ ...prev, birthDate: stored }))
    const err = validateDateParts(dd, mm, y, eraHint as 'CE' | 'BCE')
    setBirthDateError(err)
  }

  const toggleEra = () => {
    const next = birthEra === 'CE' ? 'BCE' : 'CE'
    setBirthEra(next)
    const m = birthDateInput.match(/^(\d{1,2})-(\d{1,2})-(?:-)?(\d{1,6})$/)
    if (m) {
      const dd = m[1], mm = m[2], y = m[3]
      const display = next === 'BCE' ? `${dd}-${mm}--${y}` : `${dd}-${mm}-${y}`
      setBirthDateInput(display)
      const sign = next === 'BCE' ? '-' : ''
      setFormData(prev => ({ ...prev, birthDate: `${dd}-${mm}-${sign}${y}` }))
      const err = validateDateParts(dd, mm, y, next)
      setBirthDateError(err)
    }
  }

  const validateDateParts = (dd: string, mm: string, y: string, era: 'CE' | 'BCE') => {
    const day = parseInt(dd || '0', 10)
    const mon = parseInt(mm || '0', 10)
    const yearDigits = y || ''
    if (yearDigits.length > 5) return t('chat.astro_form.errors.year_max_digits')
    if (mon && (mon < 1 || mon > 12)) return t('chat.astro_form.errors.invalid_month')
    if (day && (day < 1 || day > 31)) return t('chat.astro_form.errors.invalid_day')
    if (yearDigits) {
      const yr = parseInt(yearDigits, 10)
      if (era === 'BCE' && yr > 13200) return t('chat.astro_form.errors.bce_year_max')
      if (era === 'CE' && yr > 17191) return t('chat.astro_form.errors.ce_year_max')
      // Only once all three parts are present: does this date actually EXIST?
      // The range checks above accept 31 February and 29 February 2025. Swiss
      // Ephemeris then silently computes the rolled-over day — verified, 1986-02-31
      // produced a chart identical to 1986-03-03 — so a plausible horoscope came
      // back for a date the user never entered. The backend rejects these too.
      if (dd && mm && !isRealCalendarDate(era === 'BCE' ? -yr : yr, mon, day)) {
        return t('chat.astro_form.errors.invalid_day')
      }
    }
    return ''
  }

  const parseHumanTimeInput = (input: string) => {
    const raw = input.trim().toLowerCase()
    if (!raw) return { hhmm: '', display: '', complete: false, normalizedInput: '', suffixIntent: '' }

    const intentMatch = raw.match(/(am?|pm?)$/)
    const suffixIntent = intentMatch ? (intentMatch[0].startsWith('p') ? 'PM' : 'AM') : ''
    const isPM = /pm$/.test(raw)
    const isAM = /am$/.test(raw)
    const cleaned = raw.replace(/\s*(am|pm)\s*$/,'').replace(/\s+/g,'')

    let hours = 0
    let minutes = 0

    let normalizedInput = ''
    if (/^\d{1,2}[.:]\d{1,2}$/.test(cleaned)) {
      const [h, m] = cleaned.split(/[.:]/)
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
      if (!isAM && !isPM && m.length === 2) {
        normalizedInput = `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`
      }
    } else if (/^\d{3,4}$/.test(cleaned)) {
      const len = cleaned.length
      const h = cleaned.slice(0, len - 2)
      const m = cleaned.slice(len - 2)
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
      if (!isAM && !isPM && len === 4) {
        normalizedInput = `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`
      }
    } else if (/^\d{1,2}$/.test(cleaned)) {
      hours = parseInt(cleaned, 10)
      minutes = 0
    } else {
      return { hhmm: '', display: '', complete: false, normalizedInput: '', suffixIntent: '' }
    }

    hours = Math.max(0, Math.min(23, hours))
    minutes = Math.max(0, Math.min(59, minutes))

    if (isPM && hours < 12) hours += 12
    if (isAM && hours === 12) hours = 0

    const hh = String(hours).padStart(2,'0')
    const mm = String(minutes).padStart(2,'0')

    const hours12 = ((hours + 11) % 12) + 1
    const suffix = hours >= 12 ? 'PM' : 'AM'
    const display = `${hours12}:${mm} ${suffix}`
    const hasAmPm = isAM || isPM
    const complete = hasAmPm
      ? /^\d{1,2}([:.]\d{1,2})?$/.test(cleaned)
      : (/^\d{1,2}([:.]\d{2})$/.test(cleaned) || /^\d{4}$/.test(cleaned))
    return { hhmm: `${hh}:${mm}`, display, complete, normalizedInput, suffixIntent }
  }

  const handleBirthTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const { hhmm, display, complete } = parseHumanTimeInput(value)
    
    setBirthTimeInput(value)
    setBirthTimeDisplay(display)
    
    if (complete) {
      setFormData(prev => ({ ...prev, birthTime: hhmm }))
    }
  }

  const handleBirthTimeBlur = () => {
    const { hhmm, display, normalizedInput, suffixIntent } = parseHumanTimeInput(birthTimeInput || birthTimeDisplay)
    if (hhmm) {
      setFormData(prev => ({ ...prev, birthTime: hhmm }))
      setBirthTimeDisplay(display)
      if (normalizedInput) {
        setBirthTimeInput(suffixIntent ? `${normalizedInput} ${suffixIntent}` : normalizedInput)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setValidationBanner('')

    try {
      if (!chartFor) {
        const message = t('chat.astro_form.subject.missing_chart_for')
        setChartForError(message)
        setValidationBanner(message)
        window.requestAnimationFrame(() => {
          chartForRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          const selected = chartForRef.current?.querySelector<HTMLButtonElement>('button')
          selected?.focus()
        })
        throw new Error(message)
      }

      if (!tzState.usable) {
        const message = t('chat.birth_timezone.blocked_submit', {
          defaultValue: 'Please resolve the timezone for the birth place and time first.',
        })
        setValidationBanner(message)
        throw new Error(message)
      }

      let finalBirthTime = formData.birthTime
      if (!finalBirthTime) {
        const parsed = parseHumanTimeInput(birthTimeInput || birthTimeDisplay)
        finalBirthTime = parsed.hhmm
      }
      if (!finalBirthTime) throw new Error(t('chat.astro_form.errors.enter_valid_birth_time'))

      const errors: string[] = []
      let y = 0, m = 1, d = 1
      let canonicalBirthDate = ''
      const dmy = /^(\d{1,2})-(\d{1,2})-(-?\d{1,6})$/.exec(formData.birthDate)
      const ymd = /^(-?\d{1,6})-(\d{1,2})-(\d{1,2})$/.exec(formData.birthDate)
      if (dmy) {
        d = parseInt(dmy[1], 10)
        m = parseInt(dmy[2], 10)
        y = parseInt(dmy[3], 10)
      } else if (ymd) {
        y = parseInt(ymd[1], 10)
        m = parseInt(ymd[2], 10)
        d = parseInt(ymd[3], 10)
      } else {
        errors.push(t('chat.astro_form.errors.birth_date_format'))
      }

      if (!errors.length) {
        if (y < -13200 || y > 17191) errors.push(t('chat.astro_form.errors.birth_year_out_of_range'))
        // Must be a date that EXISTS, not merely one whose parts are in range.
        // Manual-timezone mode can otherwise reach submit with 31 February and
        // get a server error instead of the inline date message.
        if (!isRealCalendarDate(y, m, d)) errors.push(t('chat.astro_form.errors.invalid_month_day'))
      }

      if (!errors.length) {
        const absY = Math.abs(y)
        const sign = y < 0 ? '-' : ''
        const mm = String(m).padStart(2, '0')
        const dd = String(d).padStart(2, '0')
        canonicalBirthDate = `${sign}${absY}-${mm}-${dd}`
      }
      
      const timeMatch = /^([0-9]{2}):([0-9]{2})$/.exec(finalBirthTime)
      if (!timeMatch) errors.push(t('chat.astro_form.errors.birth_time_format'))
      if (timeMatch) {
        const hh = parseInt(timeMatch[1], 10)
        const mm = parseInt(timeMatch[2], 10)
        if (hh < 0 || hh > 23 || mm < 0 || mm > 59) errors.push(t('chat.astro_form.errors.birth_time_out_of_range'))
      }
      
      const lat = parseFloat(formData.latitude)
      const lon = parseFloat(formData.longitude)
      const tz = parseFloat(formData.timezoneOffset)
      // Autofill/typing fills the city text without picking a suggestion, so
      // lat/lon never resolve. Say that plainly instead of "latitude range".
      const locationUnresolved = (Number.isNaN(lat) || Number.isNaN(lon)) && !!formData.locationName.trim()
      if (locationUnresolved) {
        errors.push(t('chat.location_input.pick_from_list'))
      } else {
        if (Number.isNaN(lat) || lat < -90 || lat > 90) errors.push(t('chat.astro_form.errors.latitude_range'))
        if (Number.isNaN(lon) || lon < -180 || lon > 180) errors.push(t('chat.astro_form.errors.longitude_range'))
        if (Number.isNaN(tz) || tz < -14 || tz > 14) errors.push(t('chat.astro_form.errors.timezone_range'))
      }
      if (!formData.name.trim()) errors.push(t('chat.astro_form.errors.name_required'))
      if (errors.length) throw new Error(errors.join('; '))
      
      // Use Vite Environment Variable for backend URL
      const baseUrl = import.meta.env.VITE_APP_SERVER || 'http://localhost:8000';

      if (!baseUrl) throw new Error(t('chat.astro_form.errors.backend_not_reachable'))

      const user = auth.currentUser
      if (!user) throw new Error(t('chat.astro_form.errors.must_login_generate'))

      const headers = await getSecureHeaders()

	      const response = await fetch(`${baseUrl}/calculate/complete_horoscope_web`, {
	        method: 'POST',
	        headers,
	        body: JSON.stringify({
          name: formData.name,
          gender: formData.gender === 'male' ? 'Male' : 'Female',
          date_of_birth: canonicalBirthDate || formData.birthDate,
          time_of_birth: `${finalBirthTime}:00`,
          place_name: formData.locationName || 'Unknown',
          country: formData.country || null,
          country_name: formData.countryName || null,
          latitude: parseFloat(formData.latitude),
	          longitude: parseFloat(formData.longitude),
	          timezone_offset: parseFloat(formData.timezoneOffset),
	          // Hint only — the backend re-resolves the offset from this zone and
	          // the birth instant, and ignores the number above if they disagree.
	          timezone_name: formData.timezoneName || null,
	          timezone_source: formData.timezoneSource || null,
	          timezone_fold: formData.timezoneFold,
	          timezone_resolver_version: formData.timezoneResolverVersion,
	          ayanamsa: 'LAHIRI',
	          chart_for: chartFor,
	          relation: chartFor === 'other' ? subjectRelation : null,
	          realtime_location: realtimeContext
	            ? {
	                place_name: realtimeContext.source === 'GPS' ? 'Browser Location' : 'Approx IP Location',
                latitude: realtimeContext.lat,
                longitude: realtimeContext.lon,
                timezone_offset: realtimeContext.timezoneOffset
              }
            : null
        }),
      })

      if (!response.ok) {
        const responseClone = response.clone()
        let errorData: any = null
        let errorText: string | null = null
        try {
          errorData = await response.json()
        } catch {
          try {
            errorText = await responseClone.text()
          } catch {
            void 0
          }
        }
        let errorMessage = t('chat.astro_form.errors.failed_calculate_chart')
        if (typeof errorData?.detail === 'string') {
          errorMessage = errorData.detail
        } else if (
          errorData?.detail &&
          typeof errorData.detail === 'object' &&
          !Array.isArray(errorData.detail) &&
          typeof errorData.detail.code === 'string'
        ) {
          errorMessage = errorData.detail.code
        } else if (Array.isArray(errorData?.detail) && errorData.detail.length > 0) {
          const first = errorData.detail[0]
          if (first && typeof first === 'object' && first.msg) {
            const fieldPath = Array.isArray(first.loc) ? first.loc.slice(1).join('.') : ''
            errorMessage = fieldPath ? `${first.msg} (${fieldPath})` : first.msg
          }
        } else if (errorText && errorText.trim()) {
          errorMessage = errorText
        }
        throw new Error(errorMessage)
      }

	      const data = await response.json()

      const subject = data?.subject || data?.metadata?.subject || null
      const savedName = typeof subject?.display_name === 'string' ? subject.display_name : ''
      const savedRelation = typeof subject?.relation === 'string' ? subject.relation : ''
      if (savedName) {
        const savedRelationLabel = savedRelation
          ? t(`chat.astro_form.subject.relations.${savedRelation}`, { defaultValue: savedRelation })
          : ''
        const relationLabel = savedRelationLabel ? ` (${savedRelationLabel})` : ''
        toast({
          title: t('chat.astro_form.subject.saved_as', { name: savedName, relation: relationLabel }),
          description: t('chat.astro_form.subject.saved_description'),
          variant: 'default',
        })
      }
	      
	      toast({
        title: t('chat.astro_form.toasts.generated_title'),
        description: t('chat.astro_form.toasts.generated_description', {
          name: formData.name,
          idSuffix: data.horoscopeId ? ` ${t('chat.astro_form.toasts.generated_id_suffix', { id: data.horoscopeId })}` : '',
        }),
        variant: "default", // Changed from success as Shadcn toast usually uses default/destructive
      })

	      const enriched = { ...data, realtimeLocation: realtimeContext }
	      onChartGenerated(enriched)
	    } catch (error: unknown) {
	      // The API layer rethrows the FastAPI `detail`, so a full profile
	      // registry reaches us as the literal code "profile_limit_reached".
	      // Show the translated explanation rather than the backend string.
	      const raw = error instanceof Error ? error.message : null
	      const description =
	        raw === 'profile_limit_reached'
	          ? t('chat.astro_form.toasts.profile_limit_description')
	          : (raw ?? t('chat.astro_form.toasts.failed_description'))
      toast({
        title: t('chat.astro_form.toasts.failed_title'),
        description,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRealtimeToggle = async (checked: boolean) => {
    if (!checked) {
      setRealtimeEnabled(false)
      setRealtimeContext(null)
      setRealtimeDetecting(false)
      return
    }
    try {
      setRealtimeDetecting(true)
      const ctx = await detectUserContext()
      if (ctx) {
        setRealtimeEnabled(true)
        setRealtimeContext(ctx)
        if (ctx.source === 'GPS') {
          toast({ title: t('chat.astro_form.realtime.browser_location') })
        } else {
          const title =
            ctx.gpsError === 'PERMISSION_DENIED'
              ? t('chat.astro_form.realtime.permission_denied')
              : ctx.gpsError === 'INSECURE_CONTEXT'
                ? t('chat.astro_form.realtime.insecure_context')
              : ctx.gpsError === 'TIMEOUT'
                ? t('chat.astro_form.realtime.timeout')
                : ctx.gpsError === 'POSITION_UNAVAILABLE'
                  ? t('chat.astro_form.realtime.position_unavailable')
                  : ctx.gpsError === 'NOT_SUPPORTED'
                    ? t('chat.astro_form.realtime.not_supported')
                    : t('chat.astro_form.realtime.approx_ip_location')
          toast({ title })
        }
      } else {
        setRealtimeEnabled(false)
        setRealtimeContext(null)
        toast({ title: t('chat.astro_form.realtime.detection_failed'), variant: 'destructive' })
      }
    } catch {
      setRealtimeEnabled(false)
      setRealtimeContext(null)
      toast({ title: t('chat.astro_form.realtime.detection_failed'), variant: 'destructive' })
    } finally {
      setRealtimeDetecting(false)
    }
  }

  return (
	    <form onSubmit={handleSubmit} className="space-y-6 relative">
        {validationBanner && (
          <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
            {validationBanner}
          </div>
        )}
        <div
          ref={chartForRef}
          className={`relative rounded-2xl border px-4 py-4 transition-all ${
            chartForError ? 'border-red-500 bg-red-50/80 ring-2 ring-red-200' : 'border-amber-200 bg-amber-50/60'
          }`}
        >
          {hasResettableFormData && (
            <div className="absolute -top-3 left-3 z-10">
              <button
                type="button"
                onClick={handleResetForm}
                title={resetFormTooltip}
                aria-label={resetFormTooltip}
                className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-slate-200 transition hover:text-slate-700 hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300"
              >
                {t('chat.astro_form.reset_form')}
              </button>
            </div>
          )}
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm font-semibold text-slate-900">
                {t('chat.astro_form.subject.chart_for_label')}
              </Label>
              <p className="mt-1 text-xs text-slate-600">
                {t('chat.astro_form.subject.chart_for_helper')}
              </p>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-red-600">
              {t('chat.astro_form.subject.required')}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                setChartFor('self')
                setChartForError('')
                setValidationBanner('')
              }}
              className={`rounded-xl border px-4 py-3 text-left transition-all ${
                chartFor === 'self'
                  ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                  : 'border-slate-300 bg-white text-slate-800 hover:border-slate-500'
              }`}
              aria-pressed={chartFor === 'self'}
            >
              <div className="text-sm font-semibold">{t('chat.astro_form.subject.myself')}</div>
              <div className={`mt-1 text-xs ${chartFor === 'self' ? 'text-slate-200' : 'text-slate-500'}`}>
                {t('chat.astro_form.subject.myself_description')}
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setChartFor('other')
                setChartForError('')
                setValidationBanner('')
              }}
              className={`rounded-xl border px-4 py-3 text-left transition-all ${
                chartFor === 'other'
                  ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                  : 'border-slate-300 bg-white text-slate-800 hover:border-slate-500'
              }`}
              aria-pressed={chartFor === 'other'}
            >
              <div className="text-sm font-semibold">{t('chat.astro_form.subject.someone_else')}</div>
              <div className={`mt-1 text-xs ${chartFor === 'other' ? 'text-slate-200' : 'text-slate-500'}`}>
                {t('chat.astro_form.subject.someone_else_description')}
              </div>
            </button>
          </div>
          {chartFor === 'other' && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="subjectRelation" className="text-sm font-medium text-slate-900">
                {t('chat.astro_form.subject.relation_label')}
              </Label>
              <select
                id="subjectRelation"
                value={subjectRelation}
                onChange={(e) => setSubjectRelation(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
              >
                <option value="friend">{t('chat.astro_form.subject.relations.friend')}</option>
                <option value="sister">{t('chat.astro_form.subject.relations.sister')}</option>
                <option value="brother">{t('chat.astro_form.subject.relations.brother')}</option>
                <option value="partner">{t('chat.astro_form.subject.relations.partner')}</option>
                <option value="parent">{t('chat.astro_form.subject.relations.parent')}</option>
                <option value="child">{t('chat.astro_form.subject.relations.child')}</option>
                <option value="client">{t('chat.astro_form.subject.relations.client')}</option>
                <option value="other">{t('chat.astro_form.subject.relations.other')}</option>
              </select>
            </div>
          )}
          {chartForError && <div className="mt-3 text-xs font-medium text-red-600">{chartForError}</div>}
        </div>
	      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
            <Label htmlFor="name">{t('chat.astro_form.full_name')}</Label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <User className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
              <input
                id="name"
                name="name"
                type="text"
                placeholder={t('chat.astro_form.enter_full_name')}
                value={formData.name}
                onChange={handleInputChange}
                className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">{t('chat.astro_form.gender')}</Label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="male">{t('chat.astro_form.male')}</option>
              <option value="female">{t('chat.astro_form.female')}</option>
            </select>
          </div>
        <div className="space-y-2">
          <Label htmlFor="birthDate">{t('chat.astro_form.birth_date_label')}</Label>
          <div className={`flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${birthDateError ? 'border-red-500 focus-within:ring-red-500' : ''}`}>
            <Calendar className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
            <input
              id="birthDate"
              name="birthDate"
              type="text"
              placeholder={t('chat.astro_form.birth_date_placeholder')}
              value={birthDateInput}
              onChange={handleBirthDateInputChange}
              className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              aria-invalid={!!birthDateError}
              required
            />
            <div className="mr-3 shrink-0">
              <button type="button" onClick={toggleEra} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">{birthEra}</button>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">{t('chat.astro_form.birth_date_support_range')}</div>
          {birthDateError && <div className="text-xs text-red-600 mt-1">{birthDateError}</div>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="birthTime">{t('chat.astro_form.birth_time')}</Label>
          <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <Clock className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
            <input
              id="birthTime"
              name="birthTime"
              type="text"
              placeholder={t('chat.astro_form.birth_time_placeholder')}
              value={birthTimeInput}
              onChange={handleBirthTimeChange}
              onBlur={handleBirthTimeBlur}
              className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              required
            />
          </div>
          {birthTimeDisplay && <div className="text-xs text-gray-500 mt-1">{t('chat.astro_form.converted_prefix')} {birthTimeDisplay}</div>}
        </div>
      </div>

        <div className="border-t border-gray-100 pt-4">
        <LocationInput 
            data={formData} 
            onChange={(d) => setFormData(prev => ({ ...prev, ...d }))} 
            showInlineHelp
            timezoneSlot={
              <BirthTimezoneField
                birthDate={toIsoBirthDate(formData.birthDate)}
                birthTime={formData.birthTime}
                latitude={formData.latitude}
                longitude={formData.longitude}
                timezoneName={formData.timezoneName}
                value={formData.timezoneOffset}
                source={formData.timezoneSource}
                onChange={(next) =>
                  setFormData(prev => ({
                    ...prev,
                    timezoneOffset: next.timezoneOffset,
                    timezoneName: next.timezoneName ?? prev.timezoneName,
                    timezoneSource: next.timezoneSource,
                    timezoneFold: next.timezoneFold,
                    timezoneResolverVersion: next.timezoneResolverVersion,
                  }))
                }
                onStateChange={setTzState}
              />
            }
        />
        <div className="mt-4 flex items-start gap-3">
            <Checkbox
              id="realtime"
              checked={realtimeEnabled}
              disabled={realtimeDetecting || loading}
              onCheckedChange={(v: boolean) => handleRealtimeToggle(!!v)}
            />
            <div>
              <Label htmlFor="realtime">{t('chat.astro_form.enable_realtime_predictions')}</Label>
              <div className="text-xs text-gray-500">{t('chat.astro_form.realtime_helper')}</div>
              {realtimeDetecting && (
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />
                  {t('chat.astro_form.detecting_location')}
                </div>
              )}
            </div>
        </div>
      </div>

      {loading && (
        <HoroscopeProgressBar
          active={loading}
          durationMs={35000}
          etaLabel={t('chat.astro_form.loading_eta')}
          stages={progressStages}
        />
      )}

      <Button 
        type="submit" 
        className={`w-full text-lg py-6 transition-all duration-200 ${
          loading 
            ? 'cursor-wait bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-500/20 disabled:opacity-95' 
            : 'bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:from-indigo-700 hover:via-violet-700 hover:to-fuchsia-600'
        }`} 
        disabled={loading || tzState.status === 'resolving'}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t('chat.astro_form.generating_horoscope')}
          </>
        ) : (
          t('chat.astro_form.generate_and_chat')
        )}
      </Button>
    </form>
  )
}
export default BirthChartForm
